import { MiTHEME } from "@miplatform/theme";
import {
  CalendarEventType,
  CalendarFollowUpDisplay,
  CalendarSetStatus,
  CalendarType,
  ICalendarConfig,
  ICalendarSet,
  ITrayConfig,
  PlatformModules,
} from "@miplatform/types";
import { createUUID, getDate } from "@miplatform/utils";
import {
  FOLLOW_UP_TRAY_FILTERS,
  NEW_ORDERS_TRAY_FILTERS,
  READY_TO_SCHEDULE_TRAY_FILTERS,
  TRAY_NEWEST_FIRST_SORT,
} from "../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../ConfigCalendarSetList.types";

const DEFAULT_CAPACITY_WARNING_THRESHOLD = 3;
const DEFAULT_CAPACITY_CRITICAL_THRESHOLD = 1;

const generateFollowUpTray = (length = 0): ITrayConfig => ({
  id: createUUID(),
  name: "Follow-Up",
  displayOrder: length + 1,
  isActive: true,
  isDefault: true,
  collapsible: true,
  hideWhenEmpty: true,
  sort: TRAY_NEWEST_FIRST_SORT,
  filter: FOLLOW_UP_TRAY_FILTERS,
  headerColor: MiTHEME.palette.common.white,
  headerBackgroundColor: MiTHEME.palette.vibrant.orange_30,
  contentBackgroundColor: MiTHEME.palette.green.light,
  countBackgroundColor: MiTHEME.palette.vibrant.red_10,
  countColor: MiTHEME.palette.common.white,
});

const generateNewOrdersTray = (length = 0): ITrayConfig => ({
  id: createUUID(),
  name: "New Orders",
  displayOrder: length + 1,
  isActive: true,
  isDefault: true,
  collapsible: true,
  hideWhenEmpty: true,
  sort: TRAY_NEWEST_FIRST_SORT,
  filter: NEW_ORDERS_TRAY_FILTERS,
  headerColor: MiTHEME.palette.common.white,
  headerBackgroundColor: MiTHEME.palette.vibrant.teal_20,
  contentBackgroundColor: MiTHEME.palette.green.light,
  countBackgroundColor: MiTHEME.palette.vibrant.red_10,
  countColor: MiTHEME.palette.common.white,
});

const getDefaultCalendarType = (
  module: PlatformModules,
  eventType: CalendarEventType
): CalendarType => {
  if (module === PlatformModules.MiDispatch) {
    return CalendarType.Scheduler;
  }

  if (eventType === CalendarEventType.WorkOrder) {
    return CalendarType.PriorityScheduler;
  }

  return CalendarType.Kanban;
};

const getDefaultCalendarConfigs = (
  module: PlatformModules,
  eventType: CalendarEventType
) => {
  const calendarConfigs = [
    generateEmptyCalendarConfig({
      type: getDefaultCalendarType(module, eventType),
    }),
  ];

  return calendarConfigs;
};

export const generateReadyToScheduleTray = (length = 0): ITrayConfig => ({
  id: createUUID(),
  name: "Ready To Schedule",
  displayOrder: length + 1,
  isActive: true,
  isDefault: true,
  collapsible: true,
  hideWhenEmpty: true,
  sort: TRAY_NEWEST_FIRST_SORT,
  filter: READY_TO_SCHEDULE_TRAY_FILTERS,
  headerColor: MiTHEME.palette.common.white,
  headerBackgroundColor: MiTHEME.palette.vibrant.orange_30,
  contentBackgroundColor: MiTHEME.palette.green.light,
  countBackgroundColor: MiTHEME.palette.vibrant.red_10,
  countColor: MiTHEME.palette.common.white,
});

export const generateCustomTray = (length = 0): ITrayConfig => ({
  id: createUUID(),
  name: "Custom Follow-Up",
  displayOrder: length + 1,
  isActive: true,
  isDefault: false,
  collapsible: true,
  hideWhenEmpty: true,
  sort: TRAY_NEWEST_FIRST_SORT,
  filter: FOLLOW_UP_TRAY_FILTERS,
  headerColor: MiTHEME.palette.common.white,
  headerBackgroundColor: MiTHEME.palette.vibrant.orange_30,
  contentBackgroundColor: MiTHEME.palette.green.light,
  countBackgroundColor: MiTHEME.palette.vibrant.red_10,
  countColor: MiTHEME.palette.common.white,
});

export const generateDefaultTrays = ({
  module,
}: {
  module: PlatformModules;
}): ITrayConfig[] => {
  if (module === PlatformModules.MiInspections) {
    return [generateReadyToScheduleTray()];
  }

  if (module === PlatformModules.MiScheduling) {
    return [generateFollowUpTray()];
  }

  return [generateNewOrdersTray(), generateFollowUpTray(1)];
};

export const generateCalendarSetDefaultData = ({
  module,
  eventType,
}: {
  module: PlatformModules;
  eventType: CalendarEventType;
}): ICalendarSetData => ({
  name: "",
  status: CalendarSetStatus.Draft,
  module,
  eventType,
  canScheduleOutsideDayTime: false,
  requireManagerOverrideForOutsideDayTime: false,
  dayStartTime: getDate().hours(9).minutes(0),
  dayEndTime: getDate().hours(17).minutes(0),
  trays: generateDefaultTrays({ module }),
  workTemplateBases: [],
  schedulerUsers: [],
  assignableUsers: [],
  serviceAreas: [],
  isAutoHold: eventType === CalendarEventType.WorkOrder,
  calendarConfigs: getDefaultCalendarConfigs(module, eventType),
  workCapacities: {},
  capacityWarningThreshold: DEFAULT_CAPACITY_WARNING_THRESHOLD,
  capacityCriticalThreshold: DEFAULT_CAPACITY_CRITICAL_THRESHOLD,
});

export const generateEmptyCalendarConfig = ({
  length = 0,
  type = CalendarType.HoldsList,
  name = "",
} = {}): ICalendarConfig => ({
  id: createUUID(),
  name,
  calendarType: type,
  followUpDisplay: CalendarFollowUpDisplay.Hours,
  displayOrder: length + 1,
});

export const canActivateCalendarSet = (
  calendarSet: Partial<ICalendarSet> | Partial<ICalendarSetData>
) => {
  const isUserRequirementMet =
    calendarSet.calendarConfigs?.[0]?.calendarType === CalendarType.Scheduler
      ? (calendarSet.assignableUsers?.length || 0) > 0
      : true;

  return (
    isUserRequirementMet &&
    (calendarSet.schedulerUsers?.length || 0) > 0 &&
    (calendarSet.workTemplateBases?.length || 0) > 0
  );
};
